import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { IconWarning } from '../../../../../../assets/DesignSystem/SVGComponents';
import { NudosStaticAlertInComponent } from '../../../../../../components/NudosComponents';
import { IlogisticServiceDetails, IlogisticServiceTrelloCardData } from '../../../../../../types/services';
import { formatShortDate } from '../../../../../../utils/formatDatesAndHours';
import DelayedModalLS from 'views/services/components/ModalsLogisticsService/DelayedModalLS/DelayedModalLS';
import InfoDisplayItem from './InfoDisplayItem';
import { truncateText } from '../../../../../../utils/truncateText';
import './LogisticServiceDatesAndProgrammingDetails.scss';

const DefinitiveEstimatedDates = ({
  logisticServiceData,
  updateDataCallback
}: {
  logisticServiceData: IlogisticServiceDetails;
  updateDataCallback: () => void;
}) => {
  const location = useLocation();

  const [showEditModal, setShowEditModal] = useState(false);

  const { estimatedDates, trackingLink, trackingCode, estimatedDeliveryTime, delayed, statusName, isLate } =
    logisticServiceData;
  const { collect, delivery } = estimatedDates || {};
  const noDataText = 'Sin datos';

  const logisticServiceTrelloData = (location?.state as { logisticServiceTrelloData: IlogisticServiceTrelloCardData })
    ?.logisticServiceTrelloData;
  const collectionSinceText = collect?.since ? `${formatShortDate(collect.since)?.onlyDate}` : '';
  const collectionUntilText = collect?.until ? `${formatShortDate(collect.until)?.onlyDate}` : '';
  const collectionDatesText =
    collectionSinceText || collectionUntilText ? `${collectionSinceText} - ${collectionUntilText}` : '';

  const estimatedDeliveryTimeText = `${estimatedDeliveryTime?.since || ''} ${
    estimatedDeliveryTime?.until ? `- ${estimatedDeliveryTime?.until}` : ''
  } días hábiles`;
  const deliverySinceText = delivery?.since ? `${formatShortDate(delivery.since)?.onlyDate}` : '';
  const deliveryUntilText = delivery?.until ? `${formatShortDate(delivery.until)?.onlyDate}` : '';
  const deliveryDatesText = deliverySinceText || deliveryUntilText ? `${deliverySinceText} - ${deliveryUntilText}` : '';

  const delayedAlertOptions = {
    title: isLate
      ? 'El servicio logístico está atrasado, actualiza las fechas lo más pronto posible'
      : 'El servicio logístico está atrasado pero las fechas fueron actualizadas',
    icon: <IconWarning className="iconWarning" />
  };
  const nudosAlertColor = isLate ? 'red' : 'yellow';
  const canEditTheDates = ['activo'].includes(statusName || '');
  const displayNudosStaticAlert = isLate || delayed;
  const disableDatesUpdatingStyles = canEditTheDates ? (displayNudosStaticAlert ? 'isLate' : '') : 'disabled';
  const alertIconColor = !canEditTheDates ? 'disabled' : isLate ? 'orange' : 'blue';
  const renderStatusStylesText = !displayNudosStaticAlert
    ? 'text-nudos-text-color'
    : isLate
    ? 'textError'
    : 'textAlert';

  const closeEditDatesModal = () => setShowEditModal(false);

  const handleUpdateDates = () => {
    if (!canEditTheDates) return;
    setShowEditModal(true);
  };

  return (
    <div
      className={`DefinitiveEstimatedDates ${
        !displayNudosStaticAlert ? '' : isLate ? 'redAlertContainer' : 'yellowAlertContainer'
      }`}
    >
      <div className="definitiveEstimatedDatesTitle">Información de tracking</div>
      <div className={`definitiveEstimatedDatesForm ${deliveryDatesText || collectionDatesText ? 'addSpacing' : ''}`}>
        <div className="fistSection">
          {collectionDatesText && (
            <div className="iconContainer infoDisplayItemCustomContainer">
              <InfoDisplayItem
                oneToneIconProps={{
                  variant: 'upload',
                  standardSize: 12
                }}
                text="Estimado de recolección"
                value={collectionDatesText}
                dateCustomClassName="text-nudos-text-color"
              />
            </div>
          )}
          {deliveryDatesText && (
            <div className={`iconContainer infoDisplayItemCustomContainer ${renderStatusStylesText}`}>
              <InfoDisplayItem
                oneToneIconProps={{
                  variant: 'download',
                  standardSize: 12
                }}
                text="Estimado de entrega"
                value={deliveryDatesText ?? estimatedDeliveryTimeText}
                dateCustomClassName={renderStatusStylesText}
              />
            </div>
          )}
        </div>
        <div className="secondSection">
          <div className="iconContainer infoDisplayItemCustomContainer">
            <InfoDisplayItem
              oneToneIconProps={{
                variant: 'externalLink',
                standardSize: 12
              }}
              text="Link de rastreo"
              value={
                trackingLink ? (
                  <a className="underline" href={trackingLink} target="_blank" rel="noopener noreferrer">
                    Ir al link de rastreo
                  </a>
                ) : (
                  noDataText
                )
              }
              dateCustomClassName="text-nudos-text-color"
            />
          </div>
          <div className="iconContainer infoDisplayItemCustomContainer">
            <InfoDisplayItem
              oneToneIconProps={{
                variant: 'lock',
                standardSize: 12
              }}
              text="Código de rastreo"
              value={truncateText(trackingCode?.trim() === '' ? noDataText : trackingCode ?? '', 30)}
              dateCustomClassName="text-nudos-text-color"
            />
          </div>
        </div>
      </div>

      {displayNudosStaticAlert && (
        <NudosStaticAlertInComponent alertOptions={delayedAlertOptions} color={nudosAlertColor} />
      )}

      {canEditTheDates && (
        <div className="updateDatesButtonContainer">
          <button className={`updateDatesButton ${disableDatesUpdatingStyles}`} onClick={handleUpdateDates}>
            <span className="editDates">Editar fechas &nbsp;- &nbsp;</span>
            <div className={`reportProblem ${alertIconColor}`}>
              Reportar un problema en el servicio
              <IconWarning className="iconWarning" />
            </div>
          </button>
        </div>
      )}

      {showEditModal && (
        <DelayedModalLS
          closeAndRestoreList={closeEditDatesModal}
          logisticServiceDetails={logisticServiceData}
          closeModalAfterSubmitCallback={closeEditDatesModal}
          selectedLogisticServiceDataForModal={logisticServiceTrelloData}
          handleSuccessInUpdatingStatus={updateDataCallback}
        />
      )}
    </div>
  );
};

export default DefinitiveEstimatedDates;
